import React, { createContext, useContext, useMemo } from 'react';
import { findCatalogRecipe } from '../mocks/recipeCatalog';
import { usePersistentState } from '../hooks/usePersistentState.js';

// Every recipe the user has actually seen, keyed by id. History and Queue store
// a `recipeId` into here rather than their own copy, so a meal cooked three
// times exists once and "cook this again" hands back the real recipe instead of
// re-deriving one from its name.
//
// Library membership is not the same as saving. Anything the user opens lands
// here so it stays reusable; `savedAt` is set only when they explicitly save it.
// Planned meals are the Queue's business and have nothing to do with either.
//
// Persisted since Stage 5, alongside QueueContext and HistoryContext. This is
// the store that makes the other two mean anything after a restart: a history
// entry is a `recipeId`, so "cook again" can only hand back the real recipe if
// the recipe outlived the tab it was found in.

const RecipeLibraryContext = createContext(null);

const DAY = 24 * 60 * 60 * 1000;

// Seeded from the catalog: the recipes behind the seeded History entries, plus
// two the user saved without cooking yet.
function seedLibrary() {
  const seeded = [
    { id: 'salmon-rice-bowl' },
    { id: 'beef-tacos' },
    { id: 'veggie-pad-thai' },
    { id: 'chicken-katsu-curry' },
    { id: 'margherita-pizza' },
    { id: 'shrimp-fried-rice' },
    { id: 'beef-chili' },
    { id: 'lemon-herb-chicken' },
    { id: 'creamy-chicken-pasta', savedAt: Date.now() - 12 * DAY },
    { id: 'miso-ramen', savedAt: Date.now() - 2 * DAY },
  ];

  return seeded
    .map(({ id, savedAt = null }) => {
      const recipe = findCatalogRecipe(id);
      return recipe ? { ...recipe, savedAt } : null;
    })
    .filter(Boolean);
}

export function RecipeLibraryProvider({ children }) {
  const [recipes, setRecipes] = usePersistentState('library', seedLibrary);

  const value = useMemo(
    () => ({
      recipes,

      getRecipe: (id) => recipes.find((recipe) => recipe.id === id) ?? null,

      // A source URL identifies a recipe we already hold, so pasting the same
      // video link twice reuses it instead of re-parsing the video.
      findByUrl: (url) => {
        const target = String(url || '').trim().replace(/\/+$/, '').toLowerCase();
        if (!target) return null;
        return (
          recipes.find(
            (recipe) =>
              String(recipe.origin?.url || '').trim().replace(/\/+$/, '').toLowerCase() === target
          ) ?? null
        );
      },

      // Upsert. Called whenever a recipe is adopted, so anything the user has
      // worked with stays findable later. Never clears an existing savedAt.
      saveRecipe: (recipe) => {
        if (!recipe?.id) return;
        setRecipes((prev) => {
          const existing = prev.find((item) => item.id === recipe.id);
          if (!existing) return [...prev, { ...recipe, savedAt: recipe.savedAt ?? null }];
          return prev.map((item) =>
            item.id === recipe.id ? { ...item, ...recipe, savedAt: item.savedAt ?? recipe.savedAt ?? null } : item
          );
        });
      },

      setSaved: (id, saved) =>
        setRecipes((prev) =>
          prev.map((item) => (item.id === id ? { ...item, savedAt: saved ? Date.now() : null } : item))
        ),
    }),
    [recipes]
  );

  return <RecipeLibraryContext.Provider value={value}>{children}</RecipeLibraryContext.Provider>;
}

export function useRecipeLibrary() {
  const context = useContext(RecipeLibraryContext);
  if (!context) throw new Error('useRecipeLibrary must be used inside <RecipeLibraryProvider>');
  return context;
}
